import {
  Input,
  ViewChild,
  OnChanges,
  Component,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ComponentFactoryResolver,
} from '@angular/core';

import Swipe from 'swipejs';

import { UiFullSizeImageComponent } from '@cakeiteasy/web-ui';
import { CieUtilsModalManagerService, IMultiUrlImage, ngHelper, UiContextService } from '@cakeiteasy/web-utils';

@Component({
  standalone: false,
  selector: 'product-image-gallery',
  templateUrl: './product-image-gallery.component.html',
  styleUrls: ['./product-image-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductImageGalleryComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() images: IMultiUrlImage[] = [];
  @Input() alt = '';

  @ViewChild('swipeImages', { static: false }) swipeImages: ElementRef;

  public selectedImage: IMultiUrlImage;
  public hasImages: boolean;

  private _swipe: Swipe;
  private _viewInitialized = false;
  private _swipeInitTimeout: any;

  constructor(
    public ui: UiContextService,
    private _modalManagerService: CieUtilsModalManagerService,
    private _componentResolver: ComponentFactoryResolver,
    private _cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this._viewInitialized = true;
    this._initSwipe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'images', () => {
      this.hasImages = this.images.length > 0;

      this._syncSelectedImage();
      this._initSwipe();
    });
  }

  ngOnDestroy(): void {
    this._destroySwipe();
  }

  public galleryItemClick(image: IMultiUrlImage): void {
    this._selectImage(image);

    if (this._swipe) {
      this._swipe.slide(this._selectedImageIndex(), 300);
    }
  }

  public showSelectedImageWithSwipe(): void {
    this.showImage(
      this.selectedImage,
      this.images.map((i) => i.original),
    );
  }

  public showImage(image: IMultiUrlImage, imgUrls: string[] = null): void {
    if (!image) {
      return;
    }

    this._modalManagerService
      .show(
        UiFullSizeImageComponent,
        this._componentResolver,
        { imgUrl: image.original, imgUrls },
        {
          windowClass: 'full-size-image-modal',
        },
      )
      .catch(() => {});
  }

  private _syncSelectedImage(): void {
    if (!this.hasImages) {
      this._selectImage(null);
      return;
    }

    if (!this.selectedImage || this.images.indexOf(this.selectedImage) === -1) {
      this._selectImage(this.images[0]);
    }
  }

  private _selectImage(image: IMultiUrlImage): void {
    this.selectedImage = image;
    this._cdr.markForCheck();
  }

  private _initSwipe(): void {
    if (!this._viewInitialized) {
      return;
    }

    this._destroySwipe();

    if (!this.ui.isMobile() || !this.hasImages) {
      return;
    }

    this._swipeInitTimeout = setTimeout(() => {
      if (!this.swipeImages || !this.swipeImages.nativeElement) {
        return;
      }

      this._swipe = new Swipe(this.swipeImages.nativeElement, {
        startSlide: this._selectedImageIndex(),
        transitionEnd: (index) => {
          this._selectImage(this.images[index]);
        },
      });
    }, 100);
  }

  private _destroySwipe(): void {
    if (this._swipeInitTimeout) {
      clearTimeout(this._swipeInitTimeout);
      this._swipeInitTimeout = null;
    }

    if (this._swipe) {
      this._swipe.kill();
      this._swipe = null;
    }
  }

  private _selectedImageIndex(): number {
    const index = this.images.indexOf(this.selectedImage);
    return index === -1 ? 0 : index;
  }
}
